import React, { useCallback } from "react";
import { useScope } from "@state/scopeContext.lib";
import { useUpgradeToCatalogV3Mutation } from "@api/flatRateBilling.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useSettings } from "@components/settings/SettingsProvider";
import { LEGACY_PLUS_ENTITY_LIMIT } from "./useLegacyPlusEntityLimit";

interface Props {
    show: boolean;
    onHide: () => void;
}

export const LegacyPlusEntityLimitModal: React.FC<Props> = ({
    show,
    onHide,
}) => {
    const { workspaceId } = useScope();
    const { open: openSettings } = useSettings();
    const { error: toastError, toast } = useToaster();

    const upgradeToCatalogV3 = useUpgradeToCatalogV3Mutation(
        workspaceId ?? "",
        () => {
            // Phase 3: explicit success toast confirms the V3 landing
            // before the page re-renders with the new licensedPriceItems.
            toast("You're now on the new plan.");
            onHide();
        },
        () => {
            toastError("Failed to migrate plan. Please try again.");
        },
    );

    const handleUpdate = useCallback(async () => {
        await upgradeToCatalogV3.mutateAsync().catch(() => undefined);
    }, [upgradeToCatalogV3]);
    const handleViewPlans = useCallback(() => {
        onHide();
        openSettings(["plans"]);
    }, [onHide, openSettings]);

    return (
        <PlainModal show={show} onHide={onHide} size="md">
            <StandardModalHeader>
                <h4 className="mb-0">
                    Your Plus plan includes up to {LEGACY_PLUS_ENTITY_LIMIT}{" "}
                    entities
                </h4>
            </StandardModalHeader>
            <p>
                Your current Plus plan is no longer available and is limited to{" "}
                {LEGACY_PLUS_ENTITY_LIMIT} entities. To add more entities,
                update to the new pricing.
            </p>

            <HStack as="footer" justify="end" gap={12} className="modal-footer">
                <Button variant="secondary" onClick={handleViewPlans} size="xl">
                    View plans
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    onClick={handleUpdate}
                    loading={upgradeToCatalogV3.isPending}
                    disabled={!workspaceId}
                    size="xl"
                    data-testid="legacy-plus-update-plan-button"
                >
                    Update plan
                </ButtonWithLoader>
            </HStack>
        </PlainModal>
    );
};
